import { useEffect } from "react";

// Creates or updates a <meta> tag identified by either name="" or property="".
function upsertMeta({ name, property, content }) {
  if (!content) return;

  const selector = name ? `meta[name="${name}"]` : `meta[property="${property}"]`;
  let tag = document.head.querySelector(selector);

  if (!tag) {
    tag = document.createElement("meta");
    if (name) tag.setAttribute("name", name);
    if (property) tag.setAttribute("property", property);
    document.head.appendChild(tag);
  }

  tag.setAttribute("content", content);
}

function upsertCanonical(href) {
  if (!href) return;

  let link = document.head.querySelector('link[rel="canonical"]');

  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }

  link.setAttribute("href", href);
}

/**
 * Sets document.title plus meta description / canonical / Open Graph tags
 * for as long as the calling component is mounted, restoring the previous
 * title on unmount.
 *
 * This is a small, dependency-free stand-in for a full head-management
 * library (react-helmet, etc.) — since this is a single-page app with only
 * one true public route (the homepage), a tiny hook covers the need
 * without pulling in a new package.
 */
export default function useDocumentHead({ title, description, canonical, image }) {
  useEffect(() => {
    const previousTitle = document.title;

    if (title) document.title = title;

    upsertMeta({ name: "description", content: description });
    upsertMeta({ property: "og:title", content: title });
    upsertMeta({ property: "og:description", content: description });
    upsertMeta({ property: "og:type", content: "website" });
    if (image) upsertMeta({ property: "og:image", content: image });

    const canonicalUrl = canonical || `${window.location.origin}${window.location.pathname}`;
    upsertCanonical(canonicalUrl);
    upsertMeta({ property: "og:url", content: canonicalUrl });

    return () => {
      document.title = previousTitle;
    };
  }, [title, description, canonical, image]);
}
